import {
  SliderSingleProps,
  SliderMarks,
  SliderRangeProps,
} from "antd/es/slider";
import styled from "styled-components";
import useMeasure from "utils/hooks/useMeasure";
import { FixMeLater } from "types";
import { useEffect, useState } from "react";
import Text from "common/Typography/Text";
import useDebouncedCallback from "@shared/utils/hooks/useDebouncedCallback";
import { typography } from "@shared/common/Themes/typography";
import StyledSlider from "common/Slider/StyledSlider";

type SliderProps = {
  useDebounce?: boolean;
  showButtons?: boolean;
  maxValue?: number;
} & (SliderSingleProps | SliderRangeProps);

const Slider = ({
  useDebounce,
  onChange,
  onChangeComplete,
  showButtons,
  value,
  maxValue,
  ...props
}: SliderProps) => {
  const [localValue, setLocalValue] = useState(value);
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();

  let defaultMarksValues = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  if (width < 700) defaultMarksValues = [0, 25, 50, 75, 100];
  else if (width < 900) defaultMarksValues = [0, 10, 25, 50, 75, 90, 100];
  if (maxValue)
    defaultMarksValues = defaultMarksValues.filter(
      (value) => value <= maxValue
    );
  const defaultMarks = defaultMarksValues.reduce((acc, mark) => {
    acc![mark] = <SliderText>{mark}%</SliderText>;
    return acc;
  }, {} as SliderMarks);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const debouncedOnChange = useDebouncedCallback({ callback: onChange });
  const _onChange = (updatedValue: number | number[]) => {
    setLocalValue(updatedValue);
    useDebounce
      ? debouncedOnChange(updatedValue)
      : onChange?.(updatedValue as FixMeLater);
  };

  const _onChangeComplete = (updatedValue: number | number[]) => {
    debouncedOnChange.flush();
    onChangeComplete?.(updatedValue as FixMeLater);
  };

  return (
    <div style={{ width: "100%" }} ref={measureRef}>
      {/*@ts-ignore*/}
      <StyledSlider
        marks={showButtons ? defaultMarks : undefined}
        onChange={_onChange}
        onChangeComplete={_onChangeComplete}
        value={localValue as FixMeLater}
        max={maxValue}
        {...props}
      />
    </div>
  );
};

export default Slider;

const SliderText = styled(Text).attrs({
  $size: typography.fontSizes.XS,
  $weight: typography.fontWeights.SEMI_BOLD,
})`
  padding: 4px 8px;
  border-radius: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    font-size: 11.5px;
    font-weight: ${p.theme.typography.fontWeights.MEDIUM};
    color: inherit;
  `}
`;
